import React, { useState, useEffect } from 'react';
import styles from './CreateInvoice.styles.scss';
import cn from 'classnames';
import { Form, Formik } from 'formik';
import { FormikField } from '../../../../components/FormikField';
import { getComponentProps, getInputType } from './helpers';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Divider } from 'primereact/divider';
import {
  buyerAddressInputs,
  buyerIdentificationInputs,
  discountInput,
  logoInput,
  notesInputs,
  orderReferenceInputs,
  paidAmountInput,
  paymentDateInput,
  paymentTermsInputs,
  roundingAmountInput,
  roundingAmountSwitchInput,
  sellerAddressInputs,
  sellerIdentificationInputs,
  specialBillingCheckInput,
  specialBillingInput,
  specialScenarioInput,
  summaryEinvoiceInput,
  supplyDateInput,
  throughRCMCheckInput,
  throughRCMInput,
  precedingInvoiceNumberInput,
  INVOICE_TYPE,
  precedingInvoiceIssueDateInput,
  noteIssuingReasonInput,
  thirdPartyIdentificationInputs,
  thirdPartyAddressInputs,
  continuousSupplyInput,
  deemedSupplyInput,
  domesticSupplyInput,
  currencyInput,
  exchangeRateInput,
  prepaymentInvoiceInput,
  invoicingPeriodInputs,
  zeroRatingReasonInput,
  exemptionReasonInput,
  outOfScopeReasonInput,
} from './formConfig/inputs';
import { useCustomContext } from '../../../../context/customContext';
import { TabView, TabPanel } from 'primereact/tabview';
import { LineItemsTable } from './components/LineItemsTable';
import { TotalsTable } from './components/TotalsTable';
import {
  calculateInvoiceTotalDiscountAmount,
  calculatePaymentTotal,
  calculateTotalPayableAmount,
  formatNumberWithCurrency,
} from '../../../../helpers/invoiceHelpers';
import { Header } from './components/Header';
import { Button } from 'primereact/button';
import './CreateInvoice.css';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import DiscountDialog from './components/DiscountDialog/DiscountDialog';
import { ALL_VALIDATIONS } from './formConfig/validations';
import PaymentDialog from './components/PaymentDialog/PaymentDialog';
import { FormErrorToast } from './components/FormErrorToast';
import { Dialog } from 'primereact/dialog';
import { countries } from '../../../../helpers/countryValues';
import sendIcon from '../../../../assets/png/send-icon.png';
import { Tooltip } from 'primereact/tooltip';
import {
  SIMPLIFIED_INVOICE_TYPE_TRANSACTION,
  STANDARD_INVOICE_TYPE_TRANSACTION,
} from '../../../../config/invoice/constants';
import { getValueOrString } from './formConfig/initialValues';
import { useAuthentication } from '../../../../hooks/useAuthentication';

const CreateInvoice = ({
  type,
  category,
  setCategory,
  handleFormSubmit,
  formObject,
  taxCountry,
  loading,
  invoiceReferenceNumber,
}) => {
  const { t, i18n } = useTranslation();
  const { countryData } = useCustomContext();
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);

  return (
    <div className={cn('create-invoice', styles.main)}>
      <Header />
      <div className={cn('type-select d-flex mb-0', styles.section)}>
        <Divider />
        <div className="buttons">
          <Button
            label={t('invoice_list.full')}
            text={category !== 'standard'}
            onClick={() => setCategory('standard')}
            type="button"
            className={cn({ 'text-black': category !== 'standard' })}
          />
          <Button
            label={t('invoice_list.simplified')}
            text={category !== 'simplified'}
            type="button"
            onClick={() => setCategory('simplified')}
            className={cn({ 'text-black': category !== 'simplified' })}
          />
        </div>
        <Divider />
      </div>
      <Formik
        validateOnMount
        enableReinitialize
        validationSchema={ALL_VALIDATIONS({ t, invoiceType: type })}
        onSubmit={(e) => handleFormSubmit(e)}
        initialValues={formObject}
      >
        {(formikProps) => (
          <FormContent
            {...formikProps}
            type={type}
            category={category}
            taxCountry={taxCountry}
            loading={loading}
            handleFormSubmit={handleFormSubmit}
            t={t}
            i18n={i18n}
            countryData={countryData}
            showDiscountDialog={showDiscountDialog}
            setShowDiscountDialog={setShowDiscountDialog}
            showPaymentDialog={showPaymentDialog}
            setShowPaymentDialog={setShowPaymentDialog}
            invoiceReferenceNumber={invoiceReferenceNumber}
          />
        )}
      </Formik>
    </div>
  );
};

const FormContent = ({
  errors,
  values,
  isValid,
  setValues,
  setFieldValue,
  validateForm,
  validateField,
  setErrors,
  setTouched,
  setFieldTouched,
  type,
  category,
  taxCountry,
  loading,
  handleFormSubmit,
  t,
  i18n,
  countryData,
  showDiscountDialog,
  setShowDiscountDialog,
  showPaymentDialog,
  setShowPaymentDialog,
  invoiceReferenceNumber,
}) => {
  // Revalidate paidAmount whenever line items or prepayment items change so the
  // "exceeds total" constraint reflects the current invoice total.
  const { userInfo } = useAuthentication();

  const errorsWithoutTemps = Object.keys(errors).filter(
    (key) => key != 'tempItem',
  ).length;

  useEffect(() => {
    if (values.paidAmountSwitch) {
      validateField('paidAmount');
    }
  }, [values.items, values.invoicePrepaymentLineItems]);

  useEffect(() => {
    if (
      !['CREDIT_TRANSFER', 'DEBIT_CARD', 'CHEQUE', 'BANKERS_DRAFT'].includes(
        values.paymentMethod,
      )
    ) {
      setFieldValue('accountNumber', '');
      setFieldValue('accountName', '');
      setFieldValue('bankNameEn', '');
    }
  }, [values.paymentMethod]);

  // Reset all special scenario fields when main checkbox is unchecked
  useEffect(() => {
    if (!values.specialScenario) {
      setFieldValue('summaryEinvoice', false);
      setFieldValue('throughRCMCheck', false);
      setFieldValue('throughRCM', null);
      setFieldValue('specialBillingCheck', false);
      setFieldValue('specialBilling', null);
      setFieldValue('continuousSupply', false);
      setFieldValue('deemedSupply', false);
      setFieldValue('domesticSupply', false);
      setFieldValue('prepaymentInvoice', false);
    }
  }, [values.specialScenario, setFieldValue]);

  // Reset restricted fields when switching to simplified invoice
  useEffect(() => {
    setFieldValue('throughRCMCheck', false);
    setFieldValue('throughRCM', null);
    setFieldValue('specialBillingCheck', false);
    setFieldValue('specialBilling', null);
    setFieldValue('domesticSupply', false);
    if (category === 'simplified') {
      setFieldValue('documentTransactionType', 'simplified');
      setFieldValue(
        'invoiceTypeTransaction',
        SIMPLIFIED_INVOICE_TYPE_TRANSACTION,
      );
    } else {
      setFieldValue('documentTransactionType', 'standard');
      setFieldValue(
        'invoiceTypeTransaction',
        STANDARD_INVOICE_TYPE_TRANSACTION,
      );
    }
  }, [category, setFieldValue]);

  // Reset throughRCM radio when throughRCMCheck is unchecked
  useEffect(() => {
    if (!values.throughRCMCheck) {
      setFieldValue('throughRCM', null);
    }
  }, [values.throughRCMCheck, setFieldValue]);

  // Reset specialBilling radio when specialBillingCheck is unchecked
  useEffect(() => {
    if (!values.specialBillingCheck) {
      setFieldValue('specialBilling', null);
    }
  }, [values.specialBillingCheck, setFieldValue]);

  useEffect(() => {
    if (values.specialBilling === 'Self') {
      setFieldValue('buyerVatNumber', getValueOrString(userInfo, 'tax_number'));
      //clear seller details
      setFieldValue('sellerVatNumber', '');
      setFieldValue('sellerName', '');
      setFieldValue('sellerEnglishName', '');
      setFieldValue('sellerFlatNumber', '');
      setFieldValue('sellerBuildingName', '');
      setFieldValue('sellerBlock', '');
      setFieldValue('sellerStreetEn', '');
      setFieldValue('sellerCity', '');
      setFieldValue('sellerCountryCode', '');
      //clear third party details
      setFieldValue('thirdPartyVatNumber', '');
      setFieldValue('thirdPartyName', '');
      setFieldValue('thirdPartyEnglishName', '');
      setFieldValue('thirdPartyFlatNumber', '');
      setFieldValue('thirdPartyBuildingName', '');
      setFieldValue('thirdPartyBlock', '');
      setFieldValue('thirdPartyStreetEn', '');
      setFieldValue('thirdPartyCity', '');
      setFieldValue('thirdPartyCountryCode', '');
      setFieldValue('thirdPartyTinNumber', '');
    } else if (values.specialBilling === 'Third') {
      setFieldValue(
        'thirdPartyVatNumber',
        getValueOrString(userInfo, 'tax_number'),
      );
      //clear buyer details
      setFieldValue('buyerVatNumber', '');
      setFieldValue('buyerName', '');
      setFieldValue('buyerEnglishName', '');
      setFieldValue('buyerFlatNumber', '');
      setFieldValue('buyerBuildingName', '');
      setFieldValue('buyerBlock', '');
      setFieldValue('buyerStreetEn', '');
      setFieldValue('buyerCity', '');
      setFieldValue('buyerCountryCode', '');
    }
  }, [values.specialBilling, setFieldValue]);

  // Reset paidAmount and prepayment line items when paidAmountSwitch is turned off
  useEffect(() => {
    if (
      !values.paidAmountSwitch ||
      values.paidAmountSwitch === 'false' ||
      values.prepaymentInvoice
    ) {
      setFieldValue('paidAmount', 0);
      setFieldValue('invoicePrepaymentLineItems', []);
    }

    if (values.prepaymentInvoice) {
      setFieldValue('paidAmountSwitch', false);
    }
  }, [values.paidAmountSwitch, setFieldValue, values.prepaymentInvoice]);

  // Clear invoice-level discount records when einvoiceLevelDiscount is turned off
  useEffect(() => {
    if (!values.einvoiceLevelDiscount) {
      setFieldValue('invoiceDiscountLineItems', []);
    }
  }, [values.einvoiceLevelDiscount, setFieldValue]);

  const getGenerateButtonImgStyle = () => {
    return { width: '19px' };
  };

  return (
    <>
      <FormErrorToast />
      <Form>
        <div className={cn('d-flex flex-row', styles.section)}>
          <div className="d-flex flex-row flex-grow-1">
            <FormikField
              {...logoInput()}
              component={getInputType(logoInput().type)}
            />
            <div className="d-flex flex-column flex-grow-1">
              <div className="invoice-info d-flex flex-row">
                <div className="flex-column d-flex">
                  <p className="label">{t('createInvoice.invoice-number')}</p>
                  <p>{invoiceReferenceNumber}</p>
                </div>
                <div className="flex-column d-flex">
                  <p className="label">{t('createInvoice.invoice-date')}</p>
                  <p>{format(new Date(), 'dd MMM, yyyy')}</p>
                </div>
              </div>
              <Accordion
                collapseIcon="pi pi-plus-circle fs-5 color-theme-primary"
                expandIcon="pi pi-plus-circle fs-5 color-theme-primary"
                className="supply-and-payment-dates"
              >
                <AccordionTab header={t('add-supply-date-and-payment-date')}>
                  <FormikField {...supplyDateInput()} />
                  <FormikField {...paymentDateInput()} />
                </AccordionTab>
              </Accordion>
            </div>
          </div>

          <Divider layout="vertical" />
          <div className="d-flex flex-column">
            <FormikField {...specialScenarioInput()} />

            <div
              className={cn('special-scenario-fields', {
                invisible: !values.specialScenario,
              })}
            >
              <div className="d-flex flex-row">
                <FormikField
                  {...specialBillingCheckInput(category, setFieldValue)}
                />
                <div
                  className={cn('d-flex', {
                    invisible: !values.specialBillingCheck,
                  })}
                >
                  <Divider layout="vertical" />
                  <FormikField {...specialBillingInput(category)} />
                </div>
              </div>
              <FormikField {...summaryEinvoiceInput()} />
              <div className="d-flex flex-row">
                <FormikField
                  {...throughRCMCheckInput(category, setFieldValue)}
                />
                <div
                  className={cn('d-flex', {
                    invisible: !values.throughRCMCheck,
                  })}
                >
                  <Divider layout="vertical" />
                  <FormikField {...throughRCMInput(category)} />
                </div>
              </div>

              <FormikField {...deemedSupplyInput()} />
              <FormikField {...continuousSupplyInput()} />
              <FormikField {...domesticSupplyInput(category)} />
              <FormikField {...prepaymentInvoiceInput()} />
            </div>
          </div>
          <Divider layout="vertical" />
          <div className="d-flex flex-column flex-grow-1">
            <FormikField
              {...currencyInput(i18n.language)}
              controlProps={{
                labelProps: { className: styles.label },
              }}
            />
            {values.currency !== 'BHD' && (
              <>
                <div className="mt-5" />
                <FormikField
                  {...exchangeRateInput(values.currency)}
                  controlProps={{
                    labelProps: { className: styles.label },
                  }}
                />
              </>
            )}
          </div>
        </div>
        <div className={cn('seller-details', styles.section)}>
          <h2 className="section-title">
            {t('createInvoice.seller-details')} <Tooltip target="#tooltip" />
            <i
              id="tooltip"
              className="pi pi-info-circle"
              data-pr-tooltip={t('tooltips.supplier_details')}
            />
          </h2>
          <div className={cn(styles.fields)}>
            {[
              ...sellerIdentificationInputs({
                initialValues: values,
                language: i18n.language,
                values: values,
              }),
              ...sellerAddressInputs({
                countries: countries[i18n.language],
              }),
            ].map(
              (input) =>
                !input.hide && (
                  <FormikField
                    name={input.name}
                    component={getInputType(input.type)}
                    componentProps={{
                      className: cn(
                        input.type !== 'textarea' ? styles.field : '',
                        input.className,
                      ),
                      ...getComponentProps({ ...input, t }),
                      // disabledDates: input.disabledDates,
                    }}
                    controlProps={{
                      labelProps: { className: styles.label },
                      className: input.className,
                    }}
                    label={
                      typeof input.label === 'string'
                        ? t(input.label)
                        : t(input.label?.value, {
                            ...input.label?.placeholder,
                          })
                    }
                    tooltip={input.tooltip}
                    required={input.required}
                    key={input.name}
                  />
                ),
            )}
          </div>
        </div>
        {values.specialBillingCheck && values.specialBilling === 'Third' && (
          <div className={cn('third-party-details', styles.section)}>
            <h2 className="section-title">
              {t('createInvoice.third-party-details')}
            </h2>
            <div className={cn(styles.fields)}>
              {[
                ...thirdPartyIdentificationInputs({
                  initialValues: values,
                  language: i18n.language,
                  values,
                }),
                ...thirdPartyAddressInputs({
                  cities: countryData?.cities?.[i18n.language],
                  countries: countries[i18n.language],
                  values,
                }),
              ].map(
                (input) =>
                  !input.hide && (
                    <FormikField
                      name={input.name}
                      component={getInputType(input.type)}
                      componentProps={{
                        className: cn(
                          input.type !== 'textarea' ? styles.field : '',
                          input.className,
                        ),
                        ...getComponentProps({ ...input, t }),
                        // disabledDates: input.disabledDates,
                      }}
                      controlProps={{
                        labelProps: { className: styles.label },
                        className: input.className,
                      }}
                      label={
                        typeof input.label === 'string'
                          ? t(input.label)
                          : t(input.label?.value, {
                              ...input.label?.placeholder,
                            })
                      }
                      tooltip={input.tooltip}
                      required={input.required}
                      key={input.name}
                    />
                  ),
              )}
            </div>
          </div>
        )}
        {values.specialBilling !== 'Third' && (
          <div className={cn('buyer-details', styles.section)}>
            <h2 className="section-title">
              {t('createInvoice.buyer-details')}
            </h2>
            <div className={cn(styles.fields)}>
              {[
                ...buyerIdentificationInputs({
                  language: i18n.language,
                  invoiceType: type,
                  values,
                  category,
                }),
                ...buyerAddressInputs({
                  cities: countryData?.cities?.[i18n.language],
                  countries: countries[i18n.language],
                  values,
                  category,
                }),
              ].map(
                (input) =>
                  !input.hide && (
                    <FormikField
                      name={input.name}
                      component={getInputType(input.type)}
                      componentProps={{
                        className: cn(
                          input.type !== 'textarea' ? styles.field : '',
                          input.className,
                        ),
                        ...getComponentProps({ ...input, t }),
                        // disabledDates: input.disabledDates,
                      }}
                      controlProps={{
                        labelProps: { className: styles.label },
                        className: input.className,
                      }}
                      label={
                        typeof input.label === 'string'
                          ? t(input.label)
                          : t(input.label?.value, {
                              ...input.label?.placeholder,
                            })
                      }
                      tooltip={input.tooltip}
                      required={input.required}
                      key={input.name}
                    />
                  ),
              )}
            </div>
          </div>
        )}
        {type !== INVOICE_TYPE.INVOICE && (
          <div className={cn('credit-debit-details', styles.section)}>
            <h2 className="section-title">
              {t('createInvoice.preceding-document-information')}
            </h2>
            <div className={cn(styles.fields)}>
              {[
                precedingInvoiceNumberInput({
                  initialValues: values,
                  language: i18n.language,
                  invoiceType: type,
                }),
                precedingInvoiceIssueDateInput({
                  initialValues: values,
                  language: i18n.language,
                  invoiceType: type,
                }),
                noteIssuingReasonInput({
                  initialValues: values,
                  language: i18n.language,
                  invoiceType: type,
                }),
              ].map(
                (input) =>
                  !input.hide && (
                    <FormikField
                      {...input}
                      name={input.name}
                      component={input.component}
                      componentProps={{
                        className: cn(
                          input.type !== 'textarea' ? styles.field : '',
                          input.className,
                        ),
                        ...getComponentProps(input),
                        // disabledDates: input.disabledDates,
                      }}
                      controlProps={{
                        labelProps: { className: styles.label },
                        className: input.className,
                      }}
                      label={
                        typeof input.label === 'string'
                          ? t(input.label)
                          : t(input.label?.value, {
                              ...input.label?.placeholder,
                            })
                      }
                      tooltip={input.tooltip}
                      required={input.required}
                      key={input.name}
                    />
                  ),
              )}
            </div>
          </div>
        )}

        {/* Invoicing Period - Required for Summary and Continuous Supply */}
        {(values.summaryEinvoice || values.continuousSupply) && (
          <div className={cn('other-dates-details', styles.section)}>
            <h2 className="section-title">{t('createInvoice.other-dates')}</h2>
            <div className={cn(styles.fields)}>
              {invoicingPeriodInputs({
                values,
                setFieldValue,
                setFieldTouched,
              }).map((input) => (
                <FormikField
                  key={input.name}
                  name={input.name}
                  component={getInputType(input.type)}
                  componentProps={{
                    className: cn(styles.field, input.className),
                    ...getComponentProps(input),
                  }}
                  controlProps={{
                    labelProps: { className: styles.label },
                    className: input.className,
                  }}
                  label={t(input.label)}
                  tooltip={input.tooltip && t(input.tooltip)}
                  required={input.required}
                />
              ))}
            </div>
          </div>
        )}

        <div
          className={cn('other-details', styles.section, styles.otherDetails)}
        >
          <TabView>
            <TabPanel
              header={
                <>
                  {t('createInvoice.item-information')}{' '}
                  <span style={{ color: 'red' }}>*</span>
                </>
              }
            >
              <LineItemsTable
                taxCountry={taxCountry}
                values={values}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                validateForm={validateForm}
                setTouched={setTouched}
                setErrors={setErrors}
                setValues={setValues}
                currency={values.currency || 'BHD'}
              />
            </TabPanel>
            <TabPanel
              header={t('createInvoice.order-reference')}
              leftIcon="pi icn-plus-circle"
              contentClassName={styles.otherDetailsTabPanel}
            >
              {orderReferenceInputs().map(
                (input, i) =>
                  !input.hide && (
                    <FormikField
                      name={input.name}
                      component={getInputType(input.type)}
                      componentProps={{
                        className: cn(
                          input.type !== 'textarea' ? styles.field : '',
                          input.className,
                        ),
                        ...getComponentProps({ ...input, t }),
                        // disabledDates: input.disabledDates,
                      }}
                      controlProps={{
                        labelProps: { className: styles.label },
                        className: input.className,
                      }}
                      label={
                        typeof input.label === 'string'
                          ? t(input.label)
                          : t(input.label?.value, {
                              ...input.label?.placeholder,
                            })
                      }
                      tooltip={input.tooltip}
                      required={input.required}
                      key={input.name}
                    />
                  ),
              )}
            </TabPanel>
            <TabPanel
              header={t('createInvoice.payment-terms')}
              leftIcon="pi icn-plus-circle"
              contentClassName={styles.otherDetailsTabPanel}
            >
              {paymentTermsInputs({
                values,
                language: i18n.language,
              }).map(
                (input, i) =>
                  !input.hide && (
                    <FormikField
                      name={input.name}
                      component={getInputType(input.type)}
                      componentProps={{
                        className: cn(
                          input.type !== 'textarea' ? styles.field : '',
                          input.className,
                        ),
                        ...getComponentProps({ ...input, t }),
                        // disabledDates: input.disabledDates,
                      }}
                      controlProps={{
                        labelProps: { className: styles.label },
                        className: input.className,
                      }}
                      label={
                        typeof input.label === 'string'
                          ? t(input.label)
                          : t(input.label?.value, {
                              ...input.label?.placeholder,
                            })
                      }
                      tooltip={input.tooltip}
                      required={input.required}
                      key={input.name}
                    />
                  ),
              )}
            </TabPanel>
            <TabPanel
              header={t('createInvoice.add-note')}
              leftIcon="pi icn-plus-circle"
              contentClassName={styles.otherDetailsTabPanel}
              contentStyle={{ display: 'block' }}
            >
              {notesInputs({
                values,
                language: i18n.language,
              }).map(
                (input, i) =>
                  !input.hide && (
                    <FormikField
                      name={input.name}
                      component={getInputType(input.type)}
                      componentProps={{
                        className: cn(
                          input.type !== 'textarea' ? styles.field : '',
                          input.className,
                        ),
                        ...getComponentProps({ ...input, t }),
                        // disabledDates: input.disabledDates,
                      }}
                      controlProps={{
                        labelProps: { className: styles.label },
                        className: input.className,
                      }}
                      label={
                        typeof input.label === 'string'
                          ? t(input.label)
                          : t(input.label?.value, {
                              ...input.label?.placeholder,
                            })
                      }
                      tooltip={input.tooltip}
                      required={input.required}
                      key={input.name}
                    />
                  ),
              )}
            </TabPanel>
          </TabView>
        </div>
        <div className={cn('total-details', styles.section)}>
          <div
            className={cn('einvoice-level-discount', {
              disabled: !values.einvoiceLevelDiscount,
            })}
          >
            <FormikField {...discountInput({ setShowDiscountDialog })} />
            <div className="details">
              {values.einvoiceLevelDiscount && (
                <>
                  <Button
                    type="button"
                    link
                    label={t('createInvoice.click-here-to-see-the-details')}
                    onClick={() => setShowDiscountDialog(true)}
                  />
                  <div className="d-flex flex-column align-items-end">
                    {formatNumberWithCurrency(
                      Number(
                        calculateInvoiceTotalDiscountAmount(
                          values.invoiceDiscountLineItems,
                        ),
                      ).toFixed(3),
                      values.currency || 'BHD',
                      i18n.language,
                    )}
                    <div className="error">{errors.discountVatRateType}</div>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="totals">
            <TotalsTable
              items={values.items}
              values={values}
              currency={values.currency || 'BHD'}
            />
          </div>
          {!values.prepaymentInvoice ? (
            <div
              className={cn('paid-amount', {
                disabled: !values.paidAmountSwitch,
              })}
            >
              <FormikField {...paidAmountInput({ setShowPaymentDialog })} />
              <div className="details">
                {values.paidAmountSwitch ? (
                  <>
                    <Button
                      type="button"
                      link
                      label={t('createInvoice.click-here-to-see-the-details')}
                      onClick={() => setShowPaymentDialog(true)}
                    />
                    <div className="d-flex flex-column align-items-end">
                      {formatNumberWithCurrency(
                        Number(
                          calculatePaymentTotal(
                            values.invoicePrepaymentLineItems,
                          ),
                        ).toFixed(3),
                        values.currency || 'BHD',
                        i18n.language,
                      )}
                      <div className="error">{errors.paidAmount}</div>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          ) : null}
          <div
            className={cn('rounding-amount', {
              disabled: !values.roundingAmountSwitch,
            })}
          >
            <FormikField {...roundingAmountSwitchInput({ setFieldValue })} />
            <div className="details justify-content-end">
              {values.roundingAmountSwitch && (
                <FormikField
                  {...roundingAmountInput({
                    currency: values.currency || 'BHD',
                    language: i18n.language,
                  })}
                />
              )}
            </div>
          </div>
          <Divider layout="horizontal" />
          <div className="total d-flex flex-row justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <span className="fw-bold">{t('createInvoice.total')}</span>{' '}
              {t('createInvoice.amount-due-for-payment')}
            </div>
            <div>
              {formatNumberWithCurrency(
                Number(
                  calculateTotalPayableAmount(values.items, values),
                ).toFixed(3),
                values.currency || 'BHD',
                i18n.language,
              )}
            </div>
          </div>
          <div className={cn(styles.fields)}>
            {[
              zeroRatingReasonInput({ values, language: i18n.language }),
              exemptionReasonInput({ values, language: i18n.language }),
              outOfScopeReasonInput({ values, language: i18n.language }),
            ].map(
              (input) =>
                !input.hide && (
                  <FormikField
                    name={input.name}
                    component={getInputType(input.type)}
                    componentProps={{
                      className: cn(
                        input.type !== 'textarea' ? styles.field : '',
                        input.className,
                      ),
                      ...getComponentProps(input),
                      // disabledDates: input.disabledDates,
                    }}
                    controlProps={{
                      labelProps: { className: styles.label },
                      className: cn(
                        input.className,
                        styles.zeroRatingReasonInput,
                      ),
                    }}
                    label={
                      typeof input.label === 'string'
                        ? t(input.label)
                        : t(input.label?.value, {
                            ...input.label?.placeholder,
                          })
                    }
                    tooltip={input.tooltip}
                    required={input.required}
                    key={input.name}
                  />
                ),
            )}
          </div>
        </div>
        <div className={cn('actions', styles.section)}>
          <Button
            disabled={loading || errorsWithoutTemps > 0}
            loading={loading}
            outlined
            size="large"
            type="button"
            onClick={() => {
              setFieldValue('isCreatingItem', false);
              setFieldValue('invoiceStatus', 'IN_DRAFT');
              handleFormSubmit({
                ...values,
                invoiceStatus: 'IN_DRAFT',
              });
            }}
          >
            <span className="pi icn-floppy" style={{ fontSize: '18px' }} />
            <span>{t('createInvoice.save-draft')}</span>
          </Button>
          <Button
            loading={loading}
            disabled={loading || errorsWithoutTemps > 0}
            type="button"
            onClick={async () => {
              setFieldValue('isCreatingItem', false);
              // Wait for state update to propagate
              await new Promise((resolve) => setTimeout(resolve, 0));
              const validationErrors = await validateForm();
              if (Object.keys(validationErrors).length > 0) {
                return;
              }
              setFieldValue('invoiceStatus', 'ACCEPTED');
              handleFormSubmit({
                ...values,
                invoiceStatus: 'ACCEPTED',
              });
            }}
            size="large"
          >
            <span>{t('generate')}</span>
            <img
              src={sendIcon}
              style={getGenerateButtonImgStyle()}
              alt={t('generate')}
            />
          </Button>
        </div>
        <DiscountDialog
          isOpen={showDiscountDialog}
          setIsOpen={setShowDiscountDialog}
          items={values.invoiceDiscountLineItems}
          values={values}
          onClose={({ items }) => {
            setFieldValue('invoiceDiscountLineItems', items);
            setShowDiscountDialog(false);
          }}
        />
        <PaymentDialog
          isOpen={showPaymentDialog}
          setIsOpen={setShowPaymentDialog}
          items={values.invoicePrepaymentLineItems}
          currency={values.currency || 'BHD'}
          onClose={({ items }) => {
            setFieldValue('invoicePrepaymentLineItems', items);
            setFieldValue(
              'paidAmount',
              Number(calculatePaymentTotal(items)).toFixed(3),
            );
            setShowPaymentDialog(false);
          }}
        />
        <Dialog
          visible={loading && values.invoiceStatus !== 'IN_DRAFT'}
          showHeader={false}
          className="loading-dialog"
        >
          <div className="pi pi-spin pi-spinner" />
          <p className="desc">{t('document-generation-in-process')}</p>
        </Dialog>
      </Form>
    </>
  );
};

export default React.memo(CreateInvoice);
